import { useState, useEffect, useMemo } from "react";
import { CSVLink } from "react-csv";
import { useDispatch, useSelector } from "react-redux";
import { Table, Button, Input, Segmented, Select } from "antd";
import { AiOutlineDownload, AiOutlineEdit, AiOutlineSearch } from "react-icons/ai";
import { BsPersonCheck, BsPersonPlus, BsPersonX } from "react-icons/bs";
import { HiOutlineUsers } from "react-icons/hi";
import { Link } from "react-router-dom";

import { loadAllCustomers,updateCustomerStatus } from "../../redux/rtk/features/user/userSlice";
import { getDepartments } from "../department/departmentApis";
import { getRoles } from "../role/roleApis";
import getPermissions from "../../utils/getPermissions";
import EditUserModal from "./EditUserModal";
import "./userList.css";

const userTypeLabels = {
  1: "Department Head",
  2: "Coordinator",
};

const getUserTypeLabel = (usertype) =>
  userTypeLabels[usertype] || (usertype ? String(usertype) : "—");

const avatarColors = [
  "from-[#023f88] to-[#0b5cae]",
  "from-sky-500 to-blue-600",
  "from-indigo-500 to-violet-600",
  "from-amber-500 to-orange-500",
  "from-rose-500 to-pink-600",
];

const getInitials = (firstName, lastName) =>
  `${(firstName || "").charAt(0)}${(lastName || "").charAt(0)}`.toUpperCase() ||
  "?";

 // Define columns for the customer table
 const columns = (handleStatusToggle, departmentNames, onEdit, canEdit) => [
  {
    id: 1,
    title: "User",
    key: "fullName",
    sorter: (a, b) =>
      `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`),
    render: ({ id, firstName, lastName, email }) => (
      <div className="flex items-center gap-3">
        <div
          className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-br ${
            avatarColors[id % avatarColors.length]
          } text-sm font-bold text-white shadow`}
        >
          {getInitials(firstName, lastName)}
        </div>
        <div className="min-w-0">
          <p className="truncate font-semibold text-slate-800 dark:text-white">
            {`${firstName || ""} ${lastName || ""}`.trim() || "Unnamed user"}
          </p>
          <p className="truncate text-sm text-slate-600">{email}</p>
        </div>
      </div>
    ),
  },
  {
    id: 3,
    title: "Phone",
    dataIndex: "phone",
    key: "phone",
    render: (phone) => phone || <span className="text-slate-300">—</span>,
  },
  {
    id: 4,
    title: "Job Title",
    dataIndex: "jobTitle",
    key: "jobTitle",
    render: (jobTitle) => jobTitle || <span className="text-slate-300">—</span>,
  },
  {
    id: 5,
    title: "User Type",
    dataIndex: "usertype",
    key: "usertype",
    render: (usertype) => (
      <span
        className={`inline-flex rounded-full px-3 py-1 text-xs font-bold ${
          Number(usertype) === 1
            ? "bg-blue-100 text-[#023f88] ring-1 ring-blue-200"
            : "bg-violet-100 text-violet-800 ring-1 ring-violet-200"
        }`}
      >
        {getUserTypeLabel(usertype)}
      </span>
    ),
  },
  {
    id: 6,
    title: "Department",
    dataIndex: "departmentId",
    key: "departmentId",
    render: (departmentId) =>
      departmentNames[departmentId] || departmentId || (
        <span className="text-slate-300">—</span>
      ),
  },
  {
    id: 7,
    title: "Status",
    dataIndex: "status",
    key: "status",
    render: (status) => (
      <span
        className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold ${
          status ? "bg-green-100 text-green-800 ring-1 ring-green-200" : "bg-red-100 text-red-700 ring-1 ring-red-200"
        }`}
      >
        <span
          className={`h-1.5 w-1.5 rounded-full ${
            status ? "bg-green-500" : "bg-red-500"
          }`}
        />
        {status ? "Active" : "Inactive"}
      </span>
    ),
  },
  {
    id: 8,
    title: "Action",
    dataIndex: "id",
    key: "action",
    render: (id, record) => (
      <div className="flex justify-start items-center gap-2">
        {canEdit && (
          <Button
            className="rounded-lg inline-flex items-center gap-1.5 border-[#023f88] text-[#023f88] font-semibold"
            icon={<AiOutlineEdit />}
            onClick={() => onEdit(record)}
          >
            Edit
          </Button>
        )}
        {/* Toggle Active/Inactive Button */}
        <Button
          type={record.status ? "default" : "primary"}
          danger={!!record.status}
          className={`rounded-lg ${record.status ? "" : "cp-navy-btn"}`}
          onClick={() => handleStatusToggle(id, record.status)}
        >
          {record.status ? "Deactivate" : "Activate"}
        </Button>
      </div>
    ),
  },

];

const StatCard = ({ icon: Icon, label, value, accent }) => (
  <div className="flex items-center gap-4 rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200/70 dark:bg-slate-800 dark:ring-slate-700">
    <div
      className={`flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br ${accent} text-white shadow-md`}
    >
      <Icon className="text-2xl" />
    </div>
    <div>
      <p className="text-3xl font-extrabold leading-none text-slate-900 dark:text-white">
        {value}
      </p>
      <p className="mt-1 text-sm font-medium text-slate-600">{label}</p>
    </div>
  </div>
);

function CustomTable() {
  const dispatch = useDispatch();
  const [departments, setDepartments] = useState([]);
  const [roles, setRoles] = useState([]);
  const [editingUser, setEditingUser] = useState(null);
  const canEdit = (getPermissions() || []).includes("update-customer");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [typeFilter, setTypeFilter] = useState("all");

  // Fetch customer data from Redux store
  const { customerList, loading } = useSelector((state) => state.users);

  useEffect(() => {
    // Dispatch action to load all customers
    dispatch(loadAllCustomers());
    getDepartments()
      .then((data) => setDepartments(data || []))
      .catch((error) => console.error("Error fetching departments:", error));
    getRoles()
      .then((data) => setRoles(data || []))
      .catch((error) => console.error("Error fetching roles:", error));
  }, [dispatch]);

  const departmentNames = useMemo(
    () => Object.fromEntries(departments.map((d) => [d.id, d.name])),
    [departments]
  );

  const handleStatusToggle = async (id, status) => {
    const newStatus = !status;
    const apiUrl = newStatus ? `customer/activate/${id}` : `customer/deactivate/${id}`;
    try {
      await dispatch(updateCustomerStatus({ apiUrl, id, status: newStatus }));
      // Reload the page after successful status toggle
      window.location.reload();
    } catch (error) {
      console.log("Error updating status:", error);
    }
  };

  const users = useMemo(() => customerList || [], [customerList]);
  const activeCount = users.filter((u) => u.status).length;

  const filteredUsers = useMemo(() => {
    const term = search.trim().toLowerCase();
    return users
      .filter((u) => {
        if (statusFilter === "Active" && !u.status) return false;
        if (statusFilter === "Inactive" && u.status) return false;
        if (typeFilter !== "all" && String(u.usertype) !== typeFilter) return false;
        if (!term) return true;
        return [
          u.firstName,
          u.lastName,
          u.email,
          u.phone,
          u.jobTitle,
          departmentNames[u.departmentId],
          String(u.id),
        ]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(term));
      })
      .map((u) => ({ ...u, key: u.id }));
  }, [users, search, statusFilter, typeFilter, departmentNames]);

  return (
    <div className="max-w-7xl mx-auto py-6 md:py-8">
      {/* Header */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#011f44] via-[#023f88] to-[#0b5cae] px-6 py-8 md:px-10 text-white shadow-xl">
        <div className="absolute -top-16 -right-16 h-56 w-56 rounded-full bg-white/10 blur-2xl" />
        <div className="absolute -bottom-20 left-1/3 h-48 w-48 rounded-full bg-sky-300/20 blur-2xl" />

        <div className="relative flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-xs font-medium uppercase tracking-wide backdrop-blur-sm">
              <HiOutlineUsers />
              User Management
            </span>
            <h1 className="mt-4 text-3xl md:text-4xl font-extrabold tracking-tight text-white">
              Users List
            </h1>
            <p className="mt-2 max-w-xl text-blue-50/90">
              View every registered user, check their status and manage their
              access.
            </p>
          </div>

          <div className="flex flex-wrap gap-3">
            <Link
              to="/customer/registerAdmin"
              className="inline-flex items-center gap-2 rounded-xl bg-white px-4 py-2.5 text-sm font-semibold text-[#023f88] shadow-lg transition hover:-translate-y-0.5 hover:shadow-xl"
            >
              <BsPersonPlus className="text-lg" />
              Add User
            </Link>
            {users.length > 0 && (
              <CSVLink
                data={users}
                filename="customers"
                className="inline-flex items-center gap-2 rounded-xl bg-white/15 px-4 py-2.5 text-sm font-semibold text-white backdrop-blur-sm transition hover:bg-white/25 hover:text-white"
              >
                <AiOutlineDownload className="text-lg" />
                Download CSV
              </CSVLink>
            )}
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="mt-6 grid gap-4 sm:grid-cols-3">
        <StatCard
          icon={HiOutlineUsers}
          label="Total users"
          value={users.length}
          accent="from-[#023f88] to-[#0b5cae]"
        />
        <StatCard
          icon={BsPersonCheck}
          label="Active"
          value={activeCount}
          accent="from-green-500 to-green-600"
        />
        <StatCard
          icon={BsPersonX}
          label="Inactive"
          value={users.length - activeCount}
          accent="from-rose-500 to-red-600"
        />
      </div>

      {/* Table */}
      <div className="mt-6 rounded-2xl bg-white p-4 md:p-6 shadow-sm ring-1 ring-slate-200/70 dark:bg-slate-800 dark:ring-slate-700">
        <div className="mb-5 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <Input
            allowClear
            size="large"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            prefix={<AiOutlineSearch className="text-slate-400" />}
            placeholder="Search by name, email, phone, department…"
            className="rounded-xl lg:max-w-md"
          />
          <div className="flex flex-wrap items-center gap-3">
            <Segmented
              value={statusFilter}
              onChange={setStatusFilter}
              options={["All", "Active", "Inactive"]}
            />
            <Select
              value={typeFilter}
              onChange={setTypeFilter}
              className="min-w-[170px]"
              style={{ width: 170 }}
              options={[
                { value: "all", label: "All user types" },
                { value: "1", label: "Department Head" },
                { value: "2", label: "Coordinator" },
              ]}
            />
          </div>
        </div>

        <Table
          className="cp-users-table"
            columns={columns(handleStatusToggle, departmentNames, setEditingUser, canEdit)}
            dataSource={filteredUsers}
            loading={loading}
            rowKey="id"
            scroll={{ x: 1000 }}
            pagination={{
              pageSize: 10,
              showSizeChanger: false,
              showTotal: (total, range) =>
                `${range[0]}–${range[1]} of ${total} users`,
            }}
            locale={{
              emptyText: (
                <div className="py-10 text-center">
                  <HiOutlineUsers className="mx-auto text-4xl text-slate-300" />
                  <p className="mt-2 font-semibold text-slate-600">No users found</p>
                  <p className="text-sm text-slate-400">
                    Try a different search or filter.
                  </p>
                </div>
              ),
            }}
          />
      </div>

      <EditUserModal
        user={editingUser}
        roles={roles}
        departments={departments}
        onClose={() => setEditingUser(null)}
        onSaved={() => {
          setEditingUser(null);
          dispatch(loadAllCustomers());
        }}
      />
    </div>
  );
}

export default CustomTable;
